import { useState, useEffect } from 'react';
import { DEFAULT_DARK_THEME, DEFAULT_LIGHT_THEME } from '../../constants/themeDefaults';
import { cloneTheme } from '../../utils/themeUtils';
import ColorPicker from './ColorPicker';
import ThemePreview from './ThemePreview';
import toast from 'react-hot-toast';

/**
 * Organization Theme Editor
 *
 * Theme editor specifically for organization mandatory themes.
 * Unlike the regular ThemeEditor, this doesn't use the useTheme hook
 * and instead works with props for external state management.
 */
export default function OrganizationThemeEditor({ isOpen, onClose, initialTheme, onSave }) {
  const [editingTheme, setEditingTheme] = useState(null);
  const [activeTab, setActiveTab] = useState('background');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize editing theme when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialTheme) {
        setEditingTheme(cloneTheme(initialTheme));
      } else {
        // If no theme exists, start with dark theme as base
        setEditingTheme(cloneTheme(DEFAULT_DARK_THEME));
      }
      setHasChanges(false);
    }
  }, [isOpen, initialTheme]);

  if (!isOpen || !editingTheme) return null;

  const updateColor = (path, value) => {
    const pathParts = path.split('.');
    const updated = cloneTheme(editingTheme);

    // Navigate to the correct nested property
    let current = updated.colors;
    for (let i = 0; i < pathParts.length - 1; i++) {
      current = current[pathParts[i]];
    }
    current[pathParts[pathParts.length - 1]] = value;

    setEditingTheme(updated);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(editingTheme);
    setHasChanges(false);
    toast.success('Organization theme updated');
    onClose();
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleReset = (preset) => {
    const confirmed = window.confirm(`Reset to ${preset} theme? This will replace all your custom colors.`);
    if (confirmed) {
      const baseTheme = preset === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
      setEditingTheme({
        ...editingTheme,
        colors: cloneTheme(baseTheme).colors
      });
      setHasChanges(true);
      toast.success(`Reset to ${preset} theme`);
    }
  };

  const tabs = [
    { id: 'background', label: 'Background', icon: '🎨' },
    { id: 'text', label: 'Text', icon: '📝' },
    { id: 'border', label: 'Borders', icon: '🔲' },
    { id: 'accent', label: 'Accent', icon: '⭐' },
    { id: 'semantic', label: 'Semantic', icon: '🚦' },
    { id: 'other', label: 'Other', icon: '⚙️' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="relative w-full max-w-7xl h-[90vh] bg-gray-900 rounded-xl shadow-2xl border border-gray-700 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent" style={{ color: 'var(--color-text-on-accent)' }}>
                          Organization Theme Editor
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Customize the mandatory theme for all users in your organization
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleReset('dark')}
              className="px-3 py-2 text-sm font-medium rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
            >
              Reset to Dark
            </button>
            <button
              onClick={() => handleReset('light')}
              className="px-3 py-2 text-sm font-medium rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
            >
              Reset to Light
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Color Controls */}
          <div className="w-1/2 border-r border-gray-700 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-gray-700 px-4 pt-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Color Pickers */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'background' && (
                <div className="space-y-4">
                  <ColorPicker
                    label="Primary Background"
                    value={editingTheme.colors.background.primary}
                    onChange={(value) => updateColor('background.primary', value)}
                  />
                  <ColorPicker
                    label="Secondary Background"
                    value={editingTheme.colors.background.secondary}
                    onChange={(value) => updateColor('background.secondary', value)}
                  />
                  <ColorPicker
                    label="Tertiary Background"
                    value={editingTheme.colors.background.tertiary}
                    onChange={(value) => updateColor('background.tertiary', value)}
                  />
                  <ColorPicker
                    label="Elevated Background"
                    value={editingTheme.colors.background.elevated}
                    onChange={(value) => updateColor('background.elevated', value)}
                  />
                  <ColorPicker
                    label="Hover Background"
                    value={editingTheme.colors.background.hover}
                    onChange={(value) => updateColor('background.hover', value)}
                  />
                </div>
              )}

              {activeTab === 'text' && (
                <div className="space-y-4">
                  <ColorPicker
                    label="Primary Text"
                    value={editingTheme.colors.text.primary}
                    onChange={(value) => updateColor('text.primary', value)}
                  />
                  <ColorPicker
                    label="Secondary Text"
                    value={editingTheme.colors.text.secondary}
                    onChange={(value) => updateColor('text.secondary', value)}
                  />
                  <ColorPicker
                    label="Muted Text"
                    value={editingTheme.colors.text.muted}
                    onChange={(value) => updateColor('text.muted', value)}
                  />
                  <ColorPicker
                    label="Disabled Text"
                    value={editingTheme.colors.text.disabled}
                    onChange={(value) => updateColor('text.disabled', value)}
                  />
                  <ColorPicker
                    label="Inverse Text"
                    value={editingTheme.colors.text.inverse}
                    onChange={(value) => updateColor('text.inverse', value)}
                  />
                </div>
              )}

              {activeTab === 'border' && (
                <div className="space-y-4">
                  <ColorPicker
                    label="Default Border"
                    value={editingTheme.colors.border.default}
                    onChange={(value) => updateColor('border.default', value)}
                  />
                  <ColorPicker
                    label="Subtle Border"
                    value={editingTheme.colors.border.subtle}
                    onChange={(value) => updateColor('border.subtle', value)}
                  />
                  <ColorPicker
                    label="Strong Border"
                    value={editingTheme.colors.border.strong}
                    onChange={(value) => updateColor('border.strong', value)}
                  />
                  <ColorPicker
                    label="Accent Border"
                    value={editingTheme.colors.border.accent}
                    onChange={(value) => updateColor('border.accent', value)}
                  />
                </div>
              )}

              {activeTab === 'accent' && (
                <div className="space-y-4">
                  <ColorPicker
                    label="Primary Accent"
                    value={editingTheme.colors.accent.primary}
                    onChange={(value) => updateColor('accent.primary', value)}
                  />
                  <ColorPicker
                    label="Secondary Accent"
                    value={editingTheme.colors.accent.secondary}
                    onChange={(value) => updateColor('accent.secondary', value)}
                  />
                  <ColorPicker
                    label="Light Accent"
                    value={editingTheme.colors.accent.light}
                    onChange={(value) => updateColor('accent.light', value)}
                  />
                  <ColorPicker
                    label="Accent Hover"
                    value={editingTheme.colors.accent.hover}
                    onChange={(value) => updateColor('accent.hover', value)}
                  />
                  <ColorPicker
                    label="Accent Disabled"
                    value={editingTheme.colors.accent.disabled}
                    onChange={(value) => updateColor('accent.disabled', value)}
                  />
                </div>
              )}

              {activeTab === 'semantic' && (
                <div className="space-y-4">
                  <ColorPicker
                    label="Success"
                    value={editingTheme.colors.success}
                    onChange={(value) => updateColor('success', value)}
                  />
                  <ColorPicker
                    label="Warning"
                    value={editingTheme.colors.warning}
                    onChange={(value) => updateColor('warning', value)}
                  />
                  <ColorPicker
                    label="Error"
                    value={editingTheme.colors.error}
                    onChange={(value) => updateColor('error', value)}
                  />
                  <ColorPicker
                    label="Info"
                    value={editingTheme.colors.info}
                    onChange={(value) => updateColor('info', value)}
                  />
                </div>
              )}

              {activeTab === 'other' && (
                <div className="space-y-4">
                  <ColorPicker
                    label="Shadow Color"
                    value={editingTheme.colors.shadow}
                    onChange={(value) => updateColor('shadow', value)}
                  />
                  <ColorPicker
                    label="Hover Background"
                    value={editingTheme.colors.hover}
                    onChange={(value) => updateColor('hover', value)}
                  />
                  <ColorPicker
                    label="Active Background"
                    value={editingTheme.colors.active}
                    onChange={(value) => updateColor('active', value)}
                  />
                  <ColorPicker
                    label="Focus Color"
                    value={editingTheme.colors.focus}
                    onChange={(value) => updateColor('focus', value)}
                  />
                  <ColorPicker
                    label="Disabled"
                    value={editingTheme.colors.disabled}
                    onChange={(value) => updateColor('disabled', value)}
                  />
                  <ColorPicker
                    label="Overlay"
                    value={editingTheme.colors.overlay}
                    onChange={(value) => updateColor('overlay', value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="w-1/2 p-6 overflow-y-auto">
            <ThemePreview theme={editingTheme} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            {hasChanges && <span className="text-yellow-400">● Unsaved changes</span>}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="px-6 py-3 rounded-xl text-base font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-4 focus:ring-gray-500/50 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-3 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-500/50 shadow-lg hover:shadow-xl transition-all duration-200"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              Save Theme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
